import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { 
  doc, 
  getDoc, 
  updateDoc,
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc 
} from 'firebase/firestore';
import CreditHistory from './CreditHistory';
import { FaBell, FaDownload, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('notifications');
  const [loading, setLoading] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    emailDreamInterpretation: true,
    emailImageGeneration: true,
    pushDreamInterpretation: true,
    pushImageGeneration: true,
    newsletterSubscription: false
  });

  // Load notification settings on component mount
  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'profiles', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        // Update local state with stored notification settings
        if (userData.notificationSettings) {
          setNotificationSettings(userData.notificationSettings);
        }
      } catch (error) {
        console.error('Error fetching notification settings:', error);
      }
    };

    fetchNotificationSettings();
  }, []);

  const toggleNotificationSetting = async (setting) => {
    if (!auth.currentUser) return;

    const updatedSettings = {
      ...notificationSettings,
      [setting]: !notificationSettings[setting]
    };

    try {
      const userRef = doc(db, 'profiles', auth.currentUser.uid);
      await updateDoc(userRef, {
        notificationSettings: updatedSettings
      });

      // Update local state
      setNotificationSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      alert('Failed to update notification settings');
    }
  };

  const handleExportUserData = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // Collect all user-related data
      const collections = [
        'posts', 
        'interpretations', 
        'imageGenerations', 
        'profiles'
      ];

      const exportData = {};

      for (const collectionName of collections) {
        const q = query(
          collection(db, collectionName), 
          where('userId', '==', auth.currentUser.uid)
        );
        const snapshot = await getDocs(q);
        
        exportData[collectionName] = snapshot.docs.map(doc => doc.data());
      }

      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dreamarium_user_data.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Data export error:', error);
      alert('Failed to export user data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you absolutely sure? This will permanently delete all your data.')) {
      return;
    }

    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // Delete all user-related documents
      const collections = [
        'posts', 
        'interpretations', 
        'imageGenerations', 
        'profiles'
      ];

      for (const collectionName of collections) {
        const q = query(
          collection(db, collectionName), 
          where('userId', '==', auth.currentUser.uid)
        );
        const snapshot = await getDocs(q);
        
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      }

      // Delete the user account
      await auth.currentUser.delete();
      
      // Redirect to home or login page
      window.location.href = '/';
    } catch (error) {
      console.error('Account deletion error:', error);
      alert('Failed to delete account. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {['notifications', 'credits', 'data'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-4 text-sm font-medium uppercase tracking-wide 
                ${activeTab === tab 
                  ? 'text-purple-600 border-b-2 border-purple-600' 
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              {tab === 'notifications' ? 'Notifications' : 
               tab === 'credits' ? 'Credit History' : 
               'Data Management'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaBell className="mr-2 text-purple-500" /> Notification Preferences
              </h2>
              
              {/* Email Notifications */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-3">Email Notifications</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>Dream Interpretation</span>
                    <button onClick={() => toggleNotificationSetting('emailDreamInterpretation')}>
                      {notificationSettings.emailDreamInterpretation 
                        ? <FaToggleOn className="text-2xl text-green-500" /> 
                        : <FaToggleOff className="text-2xl text-gray-400" />}
                    </button>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Image Generation</span>
                    <button onClick={() => toggleNotificationSetting('emailImageGeneration')}>
                      {notificationSettings.emailImageGeneration 
                        ? <FaToggleOn className="text-2xl text-green-500" /> 
                        : <FaToggleOff className="text-2xl text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Push Notifications */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-3">Push Notifications</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>Dream Interpretation</span>
                    <button onClick={() => toggleNotificationSetting('pushDreamInterpretation')}>
                      {notificationSettings.pushDreamInterpretation 
                        ? <FaToggleOn className="text-2xl text-green-500" /> 
                        : <FaToggleOff className="text-2xl text-gray-400" />}
                    </button>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Image Generation</span>
                    <button onClick={() => toggleNotificationSetting('pushImageGeneration')}>
                      {notificationSettings.pushImageGeneration 
                        ? <FaToggleOn className="text-2xl text-green-500" /> 
                        : <FaToggleOff className="text-2xl text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Newsletter */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Newsletter Subscription</span>
                  <button onClick={() => toggleNotificationSetting('newsletterSubscription')}>
                    {notificationSettings.newsletterSubscription 
                      ? <FaToggleOn className="text-2xl text-green-500" /> 
                      : <FaToggleOff className="text-2xl text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'credits' && <CreditHistory />}

          {activeTab === 'data' && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center text-purple-600">
                  <FaDownload className="mr-2" /> Export Your Data
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Download all your personal data in a machine-readable format.
                </p>
                <button 
                  onClick={handleExportUserData}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {loading ? 'Exporting...' : 'Export Data'}
                </button>
              </div>

              <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg border border-red-200 dark:border-red-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center text-red-600">
                  <FaTrash className="mr-2" /> Delete Account
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button 
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  {loading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;